import * as moment from 'moment';

import { create, v, w } from '@dojo/framework/core/vdom';
import { createICacheMiddleware } from '@dojo/framework/core/middleware/icache';
import { theme } from '@dojo/framework/core/middleware/theme';
import has from '@dojo/framework/core/has';
import RadioGroup from '@dojo/widgets/radio-group';

import { DateInput } from '@holmescorp/base-widgets/date-input';
import { Keys, convertToBoolean } from '@holmescorp/common';
import { createNlsMiddleware, examdateChange, userstate, examdateHide, landingChange } from 'lcc-state';

import { logUser } from '@holmescorp/logger';

import css from '../css/exam-countdown.m.css';

interface ExamCountdownIcache {
	examdate: string;
	hideCountdown: boolean;
	hasExam: boolean;
}

const icache = createICacheMiddleware<ExamCountdownIcache>();
const nls = createNlsMiddleware('settings');
const factory = create({
	icache,
	theme,
	nls,
	userstate
});

export const ExamCountdown = factory(({ middleware: { icache, theme, nls, userstate } }) => {
	const {
		isPlaceholder,
		messages: {
			examcountdown_date = '[settings] examcountdown_date',
			examcountdown_intro = '[settings] examcountdown_intro',
			fieldset_examcountdown = '[settings] fieldset_examcountdown',
			hide_countdown = '[settings] hide_countdown',
			info_gathering_is_us_text = '',
			has_exam_text = 'Will you be taking an exam?'
		}
	} = nls();

	if (isPlaceholder) {
		return null;
	}

	const themeCss = theme.classes(css);

	const { get, path } = userstate;
	const selectedDate = get(path('examdate'));
	// use selected date if it has value and is greater than today, otherwise blank
	const examdate = icache.getOrSet(
		'examdate',
		selectedDate && Math.ceil(moment(selectedDate).diff(moment(), 'days', true)) > 0
			? moment(selectedDate).format('YYYY-MM-DD')
			: ''
	);
	const hideCountdown = icache.getOrSet('hideCountdown', (): boolean => {
		return (get(path('toolstate', 'countdown')) || { hide: false }).hide;
	});
	const hasExam = icache.getOrSet('hasExam', (): boolean => {
		const landingState = get(path('toolstate', 'landing'));
		if (landingState.has_exam === undefined) {
			return true;
		}
		return landingState.has_exam;
	});

	const additionalContent = () => {
		if (!info_gathering_is_us_text || !has('shrm2024')) {
			return null;
		}

		const landingState = get(path('toolstate', 'landing'));
		const isExamInUS = landingState.examinus;

		return v('div', {}, [
			v('fieldset', {}, [
				v('legend', {}, [info_gathering_is_us_text]),
				w(RadioGroup, {
					name: 'examinus',
					initialValue: `${isExamInUS}`,
					options: [
						{ label: 'Yes', value: 'true' },
						{ label: 'No', value: 'false' }
					],
					onValue: (value) => {
						landingChange({ examinus: convertToBoolean(value) });
					}
				})
			])
		]);
	};

	const cls = hideCountdown ? 'fa-check-square' : 'fa-square';

	return v('div', {}, [
		v('fieldset', {}, [
			v('legend', {}, [has_exam_text]),
			w(RadioGroup, {
				name: 'has_exam',
				initialValue: `${hasExam}`,
				options: [
					{ label: 'Yes', value: 'true' },
					{ label: 'No', value: 'false' }
				],
				onValue: (value) => {
					const is_using_exam = convertToBoolean(value);
					landingChange({ has_exam: is_using_exam });

					!is_using_exam && examdateChange({ examdate: '' });
					examdateHide({ hide: !is_using_exam });
					icache.set('hideCountdown', !is_using_exam);
					icache.set('hasExam', is_using_exam);
				}
			})
		]),
		hasExam
			? v('div', {}, [
					v('fieldset', {}, [
						v('legend', {}, [fieldset_examcountdown]),
						v('div', {}, [
							v('p', { innerHTML: examcountdown_intro }),
							!hideCountdown
								? w(
										DateInput,
										{
											name: 'examCountdownSelectedDate',
											initialValue: examdate,
											onValue: (date: string) => {
												const newDate = moment(date);
												if (newDate.diff(moment(), 'days', true) > 0) {
													const examdate = newDate.format('YYYY-MM-DD');
													logUser.info(() => `exam date selected: ${examdate}`);
													examdateChange({ examdate });
													icache.set('examdate', examdate);
												}
											},
											iconLabel: 'Open exam date calendar'
										},
										[examcountdown_date]
								  )
								: null
						]),
						v(
							'div',
							{
								key: 'hide-countdown',
								classes: [themeCss.hideCountdownBlock],
								xqa: 'setting-hide-countdown'
							},
							[
								v(
									'div',
									{
										classes: [],
										tabIndex: 0,
										role: 'checkbox',
										'aria-checked': hideCountdown ? 'true' : 'false',
										'aria-labelledby': 'hideLabel',
										onclick: (event) => {
											event && event.stopPropagation();
											const hide = !hideCountdown;
											examdateHide({ hide });
											icache.set('hideCountdown', hide);
										},
										onkeydown: (event: KeyboardEvent) => {
											const hide = !hideCountdown;
											switch (event.key) {
												case Keys.Enter:
												case Keys.Space:
												case Keys.Spacebar:
													event.preventDefault();
													examdateHide({ hide });
													icache.set('hideCountdown', hide);
											}
										}
									},
									[v('i', { classes: ['far', 'fa-lg', cls] })]
								),
								v('div', { id: 'hideLabel' }, [hide_countdown])
							]
						)
					]),
					additionalContent()
			  ])
			: null
	]);
});
